import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mic,
  StopCircle,
} from 'lucide-react';
import {
  appendSilence,
  buildHardwareDetails,
  createUuid,
  DEFAULT_WS_URL,
  flatten,
  MAX_VOICE_DURATION_MS,
  POST_SPEECH_PADDING_MS,
  resampleBuffer,
  SILENCE_TIMEOUT_MS,
  TARGET_RATE,
  VAD_THRESHOLD,
} from './audioMatcherUtils';
import ResultSegment from './ResultSegment';
import './AudioMatcher.css';

const MODEL_ID = 'W2V2v1.2';

export default function AudioMatcher({
  selectedGameTag,
}) {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [segments, setSegments] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [preprocessEnabled, setPreprocessEnabled] = useState(true);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const muteRef = useRef(null);
  const streamRef = useRef(null);
  const chunkBufferRef = useRef([]);
  const recordingRef = useRef(false);
  const lastVoiceRef = useRef(null);
  const sendTimerRef = useRef(null);
  const lastSendTimeRef = useRef(null);
  const audioStartedRef = useRef(false);
  const controlReadyRef = useRef(false);

  const userid = useMemo(() => createUuid(), []);
  const sessionid = useMemo(() => createUuid(), []);
  const FrontendDT = useMemo(() => new Date().toString(), []);

  const cleanupResources = () => {
    recordingRef.current = false;
    chunkBufferRef.current = [];
    lastVoiceRef.current = null;
    sendTimerRef.current = null;
    lastSendTimeRef.current = null;
    audioStartedRef.current = false;
    controlReadyRef.current = false;

    try { processorRef.current && (processorRef.current.onaudioprocess = null); } catch {}
    try { processorRef.current?.disconnect(); } catch {}
    try { muteRef.current?.disconnect(); } catch {}
    try { sourceRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    try { wsRef.current?.close(); } catch {}
    try { (streamRef.current?.getTracks?.() || []).forEach(track => track.stop()); } catch {}

    processorRef.current = null;
    muteRef.current = null;
    sourceRef.current = null;
    audioCtxRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
  };

  useEffect(() => cleanupResources, []);

  const addInfoSegment = raw => {
    setSegments(prev => [
      {
        id: Date.now() + Math.random(),
        rawData: raw,
        predicted: '',
        transcript: null,
        accuracy: null,
        matches: [],
        latency: null,
        pitch: null,
        isChild: null,
        loudness: null,
        errorRegion: null,
        repetitions: null,
        speechRate: null,
        status: raw?.status || raw?.error || 'info',
        expected: '',
        chunkId: null,
        modelId: null,
        processingMode: null,
        deviceAudio: null,
        actionWordLabel: null,
        greedyPathConfidence: null,
        perPhonemeConfidence: [],
        isInfo: true,
      },
      ...prev,
    ]);
  };

  const normalizeSegment = data => {
    const phonemeArr = Array.isArray(data.predicted_phonemes) ? data.predicted_phonemes : [];
    const expectedArr = Array.isArray(data.expected_phonemes) ? data.expected_phonemes : [];
    const matches = Array.isArray(data.matches) ? data.matches : [];

    return {
      id: Date.now() + Math.random(),
      rawData: data,
      predicted: phonemeArr.join(' '),
      expected: expectedArr.join(' '),
      transcript: data.transcript || data.text || data.predicted_text || null,
      accuracy: typeof data.weighted_accuracy === 'number' ? data.weighted_accuracy : null,
      matches,
      latency: lastSendTimeRef.current ? Date.now() - lastSendTimeRef.current : null,
      pitch: data.pitch_hz ?? null,
      isChild: data.is_child_voice ?? null,
      loudness:
        typeof data.loudness_db_weighted === 'number' ? Math.round(data.loudness_db_weighted) : null,
      errorRegion: data.error_region ?? null,
      repetitions: Array.isArray(data.repetitions_detected)
        ? data.repetitions_detected.length
        : data.repetitions_detected ?? null,
      speechRate: data.speech_rate ?? null,
      status: data.status || 'ok',
      chunkId: data.chunk_id || data.timestamp || null,
      modelId: data.model_id || data.model || MODEL_ID,
      processingMode: data.processing_mode || null,
      deviceAudio: typeof data.device_audio === 'boolean' ? data.device_audio : null,
      actionWordLabel: data.action_word_label || null,
      greedyPathConfidence: data.greedy_path_confidence ?? null,
      perPhonemeConfidence: Array.isArray(data.per_phoneme_confidence) ? data.per_phoneme_confidence : [],
      isInfo: false,
    };
  };

  const start = async () => {
    if (starting || recording) return;

    setStarting(true);
    setSegments([]);

    const ws = new WebSocket(DEFAULT_WS_URL);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    const startAudioCapture = async () => {
      if (audioStartedRef.current) return;
      audioStartedRef.current = true;

      let audioCtx;
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = audioCtx;
        if (audioCtx.state === 'suspended') {
          try { await audioCtx.resume(); } catch {}
        }
      } catch (error) {
        addInfoSegment({ error: `Audio context error: ${String(error)}` });
        cleanupResources();
        setStarting(false);
        setRecording(false);
        setConnected(false);
        return;
      }

      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        addInfoSegment({ error: `Mic permission denied: ${String(error)}` });
        cleanupResources();
        setStarting(false);
        setRecording(false);
        setConnected(false);
        return;
      }

      sourceRef.current = audioCtx.createMediaStreamSource(streamRef.current);
      processorRef.current = audioCtx.createScriptProcessor(4096, 1, 1);
      muteRef.current = audioCtx.createGain();
      muteRef.current.gain.value = 0;

      sourceRef.current.connect(processorRef.current);
      processorRef.current.connect(muteRef.current);
      muteRef.current.connect(audioCtx.destination);

      processorRef.current.onaudioprocess = event => {
        if (!controlReadyRef.current || ws.readyState !== WebSocket.OPEN) return;

        const input = event.inputBuffer.getChannelData(0);
        const rms = Math.sqrt(input.reduce((sum, value) => sum + value * value, 0) / input.length);
        const now = Date.now();

        if (rms > VAD_THRESHOLD) {
          if (!recordingRef.current) {
            recordingRef.current = true;
            chunkBufferRef.current = [];
            sendTimerRef.current = now;
          }
          lastVoiceRef.current = now;
        }

        if (recordingRef.current) {
          const frameCopy = new Float32Array(input.length);
          frameCopy.set(input);
          chunkBufferRef.current.push(frameCopy);

          const elapsed = now - sendTimerRef.current;
          const silentFor = lastVoiceRef.current ? now - lastVoiceRef.current : 0;

          if (elapsed >= MAX_VOICE_DURATION_MS || silentFor >= SILENCE_TIMEOUT_MS) {
            recordingRef.current = false;
            const fullChunk = flatten(chunkBufferRef.current);
            chunkBufferRef.current = [];
            const resampled = resampleBuffer(fullChunk, audioCtx.sampleRate, TARGET_RATE);
            const padded = appendSilence(resampled, POST_SPEECH_PADDING_MS, TARGET_RATE);
            lastSendTimeRef.current = Date.now();
            try {
              ws.send(JSON.stringify({ type: 'chunk_meta', device_audio: false }));
              ws.send(padded.buffer);
            } catch {}
          }
        }
      };

      setRecording(true);
      setStarting(false);
    };

    ws.onopen = () => {
      const payload = {
        gameid: selectedGameTag || 'test001',
        parentGameID: selectedGameTag || 'default',
        userid,
        sessionid,
        FrontendDT,
        appVersion: 'phoneme-match-ui',
        hardware_details: buildHardwareDetails(TARGET_RATE),
        model: MODEL_ID,
        preprocess_audio_enabled: preprocessEnabled,
      };

      ws.send(JSON.stringify(payload));
    };

    ws.onmessage = event => {
      let data = event.data;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
        else if (data instanceof ArrayBuffer) {
          const text = new TextDecoder('utf-8').decode(new Uint8Array(data));
          data = JSON.parse(text);
        }
      } catch {
        addInfoSegment({ error: 'Non-JSON message received.' });
        return;
      }

      if (data?.error) {
        addInfoSegment(data);
        if (!controlReadyRef.current) {
          setStarting(false);
          setConnected(false);
        }
        return;
      }

      if (data?.status === 'control parameters set') {
        controlReadyRef.current = true;
        setConnected(true);
        startAudioCapture();
        return;
      }

      if (data?.status === 'chunk_meta_set') return;

      if (data?.status === 'no_human_speech_detected') {
        addInfoSegment(data);
        return;
      }

      const hasResult =
        Array.isArray(data.predicted_phonemes) ||
        data.expected_phonemes ||
        data.weighted_accuracy != null ||
        data.status === 'device_audio_ignored';

      if (!hasResult) {
        if (data?.status) addInfoSegment(data);
        return;
      }

      const newSeg = normalizeSegment(data);
      setSegments(prev => [newSeg, ...prev]);
    };

    ws.onclose = () => {
      setConnected(false);
      setRecording(false);
      setStarting(false);
    };

    ws.onerror = () => {
      addInfoSegment({ error: 'WebSocket connection error.' });
      setStarting(false);
    };
  };

  const stop = () => {
    cleanupResources();
    setRecording(false);
    setConnected(false);
    setStarting(false);
  };

  const toggleJson = id => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyJson = rawData => {
    navigator.clipboard?.writeText(JSON.stringify(rawData, null, 2)).catch(() => {});
  };

  const visibleSegments = segments.filter(segment => (
    segment.isInfo ||
    segment.predicted ||
    segment.transcript ||
    segment.expected ||
    segment.status
  ));

  return (
    <div className="app-card">
      <div className="header-row">
        <h2 className="title">Phoneme Matcher</h2>

        <div className="controls-right">
          <div className={`model-pill fixed-model ${connected ? 'disabled' : ''}`}>
            <span className="model-pill-label">{MODEL_ID}</span>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={preprocessEnabled}
            className={`toggle-switch ${preprocessEnabled ? 'on' : 'off'} ${connected ? 'disabled' : ''}`}
            onClick={() => !connected && setPreprocessEnabled(value => !value)}
            disabled={connected}
            title="Preprocessing"
          >
            <span className="toggle-track">
              <span className="toggle-thumb" />
            </span>
            <span className="toggle-label">Preprocessing</span>
          </button>
        </div>
      </div>

      <div className="audio-matcher">
        <div className="buttons">
          <motion.button onClick={start} disabled={recording || starting} className="start-btn">
            <Mic size={18} /> {starting ? 'Starting...' : 'Start'}
          </motion.button>
          <motion.button onClick={stop} disabled={!recording && !starting} className="stop-btn">
            <StopCircle size={18} /> Stop
          </motion.button>
        </div>

        <div className={`status ${connected ? 'on' : 'off'}`}>
          {connected
            ? `Ready • ${selectedGameTag || 'No tag selected'}`
            : starting
              ? 'Connecting to production API...'
              : 'Disconnected'}
        </div>

        <div className="segments">
          {visibleSegments.length === 0 ? (
            <div className="segment-empty">
              {connected ? 'Listening... speak to see predictions here.' : 'Press Start to connect and speak.'}
            </div>
          ) : (
            visibleSegments.map((segment, index) => {
              const isExpanded = expandedIds.has(segment.id);
              return (
                <ResultSegment
                  key={segment.id}
                  segment={segment}
                  index={index}
                  totalSegments={visibleSegments.length}
                  isExpanded={isExpanded}
                  toggleJson={toggleJson}
                  copyJson={copyJson}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
